// Leads — bare contact capture, unioned across its sources at query time.
//
// ⚠️ THIS IS NOT THE APPLICATIONS PAGE. "Applications" (the old Leads tab)
// serves submitted mortgage applications. This holds people who have given
// Doorly a name and an email and nothing more. No credit band, no purchase
// price, no employment — if any of those appear here, the boundary has leaked.
//
// ⚠️ ONE ROW PER PERSON, NOT PER RECORD. Someone can arrive as a registration
// AND a partner invite; those are two origins of one human, each keeping its own
// source chip.
//
// ⚠️ CONVERSION MEANS ONE THING: AN APPLICATION EXISTS. It is derived from
// `applications`, never from either source's own flag — leads.converted_at is
// written only by the submission transaction, and buyer_referrals.status =
// 'registered' means an ACCOUNT was made. Where a source's record disagrees the
// row says so as a data-quality note; the application stays the authority.
import React, { useState, useEffect, useCallback } from 'react';
import RightDrawer from '../components/RightDrawer';
import AdminLayout from '../components/AdminLayout';
// 🚨 READ-ONLY FOR partner_success. The server already refuses every write on
// this router — SCOPED_BORROWER_READ_ROUTES is GET-only, so archive, notes and
// email fall through to the blanket denial. This hides the buttons so the page
// does not OFFER what it will refuse: the dead-affordance shape every report
// from this role has turned out to be.
//
// ⚠️ IT HIDES RATHER THAN DISABLES. A disabled button still says "you could do
// this if something were different", which is a question nobody can answer from
// the screen.
import { isBorrowerViewReadOnly, currentStaffRole } from '../constants/staffRoleAccess';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
// ⚠️ MODULE-LEVEL: the role cannot change without a reload, so reading it per
// render would invite a staleness question that does not exist.
const READ_ONLY = isBorrowerViewReadOnly(currentStaffRole());
const authHeaders = (t) => ({ Authorization: `Bearer ${t}` });

const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null);
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);

// ⚠️ SOURCE IS WHERE THEY CAME FROM. "Registration" used to live here, but
// registering is not an origin — it is something that happened to a lead after
// arriving. It moved to the Status column, and anything not attributable to a
// real campaign reads "Other" rather than borrowing a status word.
//
// Only two origins are genuinely trackable today. When campaigns become
// attributable, add them here; until then "Other" is honest, and the Source
// column carrying little information IS the true state.
//
// 🚨 broker_submission IS THE MOST ATTRIBUTABLE ORIGIN THERE IS, and it was
// missing — so an LO's submitted borrower fell through to "Other". A named
// partner typed the application and sent it; there is nothing uncertain about
// where that person came from. Added here so the Source column says so and the
// row can be filtered FOR rather than merely appearing.
const TRACKABLE_SOURCES = ['eva_chat', 'partner_invite', 'direct', 'broker_submission'];
// 🔑 'registration' RENDERS AS "Direct sign-up" TOO, and only ever appears when
// it is a person's ONLY origin — the server hides it otherwise. Six rows predate
// the switch to 'direct' and cannot be rewritten (trg_leads_one_way blocks
// UPDATE on converted leads), so they are labelled as what they actually meant.
const SOURCE_LABEL = {
  partner_invite: 'Partner invite',
  eva_chat: 'Eva chat',
  direct: 'Direct sign-up',
  registration: 'Direct sign-up',
  // ⚠️ "Broker submission", NOT "Broker referral". A referral is an
  // introduction; this is a complete application the originator typed and sent,
  // waiting on the borrower to confirm it. The two are different work.
  broker_submission: 'Broker submission',
};
const SOURCE_STYLE = {
  partner_invite: { bg: '#ecfdf5', fg: '#065f46' },
  eva_chat:       { bg: '#eff6ff', fg: '#1e40af' },
  direct:         { bg: '#fefce8', fg: '#854d0e' },
  // 🔑 THE SAME AMBER THE OTHER TWO SCREENS USE for this row type, so a staff
  // member moving between the leads page, the referrals page and the legacy
  // dashboard sees one colour mean one thing.
  broker_submission: { bg: '#fffbeb', fg: '#92400e' },
  other:          { bg: '#f3f4f6', fg: '#4b5563' },
};
const sourceKey = (src) => {
  if (src === 'registration') return 'direct';
  return TRACKABLE_SOURCES.includes(src) ? src : 'other';
};

// ── 🚨 ONE LIST OF CHIPS, FROM THE SERVER ────────────────────────
//
// The TABLE suppressed non-trackable origins when a trackable one existed; the
// EXPANDED DETAIL did not. So Loc Tran (user 354) read as one clean "Partner
// invite" in the table and showed "Partner invite" + "Other" the moment anyone
// opened him — two filters, two answers, from the same data.
//
// 🔑 display_origins is computed server-side now, so both surfaces show the
// same thing and the rule lives in one place. The `|| p.origins` fallback only
// matters against a backend that predates the field.
const chipOrigins = (p) => p.display_origins || p.origins || [];

// ── 🚨 STATUS COMES FROM THE SERVER NOW, AND IT IS DERIVED ────────────
//
// This read `origins.some(o => o.registered_at)`. NEITHER ARM OF THE UNION
// COULD EVER SUPPLY IT: the partner-invite arm hardcodes NULL by design, and
// leads.registered_at had no live writer — routes/auth.js never set it. So a
// borrower who had just registered read "Not Registered" while the row beside
// them carried their user id, and an every-boot UPDATE silently corrected it on
// the next deploy.
//
// 🔑 utils/leadRegistration.js now answers it from three pointers into `users`,
// server-side, in one query, for every reader. A rule that lives in a component
// is a rule the next reader of this data will get differently.
//
// ⚠️ null MEANS UNKNOWN. The server sends null when the derivation itself
// failed, and "Not Registered" is not the right thing to say about that.
function leadStatus(person) {
  if (person.registered === null || person.registered === undefined) {
    return { text: 'Status unavailable', bg: '#fef2f2', fg: '#991b1b' };
  }
  return person.registered
    ? { text: 'Registered/Pre-Qualified', bg: '#eef2ff', fg: '#3730a3' }
    : { text: 'Not Registered', bg: '#f9fafb', fg: '#6b7280' };
}

// A field that was never collected reads differently from one collected empty.
// Neither is a blank cell.
function Field({ value, label }) {
  if (value) return <span style={{ color: '#111827' }}>{value}</span>;
  return (
    <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }} title={`No ${label} captured on any source`}>
      not captured
    </span>
  );
}

function SourceChip({ origin, bare = false }) {
  const s = SOURCE_STYLE[sourceKey(origin.source)];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, background: s.bg, color: s.fg,
      borderRadius: 100, padding: '3px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {SOURCE_LABEL[origin.source] || 'Other'}
      {/* Partner name inline — an invite without the partner is unworkable. */}
      {/* `bare` on the panel's source card, where the partner is the line below the chip. */}
      {!bare && origin.partner_name && <span style={{ fontWeight: 500, opacity: 0.85 }}>· {origin.partner_name}</span>}
    </span>
  );
}

// ⚠️ "source record differs" IS RETIRED FROM THIS CELL (2026-11-02). It compared each source's own converted flag
// with the application — not contact details — and beside the new "Details differ" tag it read as the same warning
// twice. Resend and Archive moved into the panel (header and footer) with the redesign.
function ConversionCell({ person }) {
  if (!person.any_converted) {
    return <span style={{ color: '#6b7280', fontSize: 13 }}>Not converted</span>;
  }
  return (
    <div>
      <span style={{
        background: '#065f46', color: '#fff', borderRadius: 100,
        padding: '2px 10px', fontSize: 11, fontWeight: 700,
      }}>CONVERTED</span>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Application on file</div>
    </div>
  );
}

// ConsentCell lived here and rendered a per-channel consent column. The column
// was removed from the table — it is reference data, not something an admin
// scans a queue by. Consent now sits in the panel's footer (ConsentLabel).


// ─────────────────────────────────────────────────────────────────────────────
// LEAD DETAIL PANEL
//
// ⚠️ "History" is CAPTURE history, not application history. application_events
// is keyed by user_id and holds zero rows for these people — events begin at
// application, which is exactly what a lead has not done. Showing an empty
// application timeline would imply nothing has happened.
// ─────────────────────────────────────────────────────────────────────────────
const KIND_ICON = { captured: '◆', invite_sent: '✉', invite_resent: '↻', converted: '✓', note: '✎', email: '➤', field_decision: '✓' };

// ── 🔑 THE LEADS SIDE PANEL, AS APPROVED (Hannah, 2026-11-02 — .scratch/reports/leads-panel-mock) ────────────────
//
// Header: name, status pill, email · phone · state, ONE primary action ("Resend invite" before registration on a
// broker submission or partner invite; "Open borrower file" once registered). Tabs: Overview (Overview + Source merged),
// Communications, Activity. Overview: Next step, Details differ (when there are any), one card per source, Team.
// Footer: queue + Archive on the left, the consent label with today's sentence as its tooltip on the right.
//
// ⚠️ STAFF WORDING IS THE MOCKUP'S. Dropped on Hannah's answers: the "This source records converted/not converted per …"
// line (answer 2) and "Opens" (answer 4). The old "source record differs" warning is retired from the screen: it
// compared each source's own converted flag, not contact details, and beside "Details differ" it read as the same
// warning twice (routes/leadCaptures.js — still in the CSV's Conflict column).
// ─────────────────────────────────────────────────────────────────────────────
const DATE_LABEL = { broker_submission: 'Submitted', partner_invite: 'Invited', eva_chat: 'Chatted', direct: 'Signed up', registration: 'Signed up' };
const fmtSent = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null);
const fmtShort = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null);
const FIELD_LABEL = { name: 'Name', phone: 'Phone', state: 'State' };
const DONE_LABEL = {
  use_application: 'Used application value',
  keep_source: 'Kept source value',
  both_valid: 'Marked reviewed (both valid)',
};
const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || '';
const rowL = { width: 132, flexShrink: 0, color: '#9ca3af', fontSize: 12 };
const rowStyle = { display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12.5, lineHeight: 1.75 };
const linkStyle = { color: '#3a6ad9', fontWeight: 600, textDecoration: 'none' };
const smallBtn = (primary, disabled) => ({
  padding: '4px 10px', fontSize: 11, fontWeight: primary ? 700 : 600, borderRadius: 6, whiteSpace: 'nowrap',
  border: primary ? 'none' : '1px solid #e5e7eb',
  background: disabled ? '#e5e7eb' : (primary ? '#3a6ad9' : '#fff'),
  color: disabled ? '#9ca3af' : (primary ? '#fff' : '#6b7280'),
  cursor: disabled ? 'not-allowed' : 'pointer',
});

function Row({ label, children, testid }) {
  return (
    <div style={rowStyle} data-testid={testid}>
      <span style={rowL}>{label}</span>
      <span style={{ color: '#111827' }}>{children}</span>
    </div>
  );
}

// One card per source (approved state A/B/C).
function SourceCard({ o, x, d }) {
  const [showChat, setShowChat] = useState(false);
  const k = sourceKey(o.source);
  const note = o.record_type === 'partner_invite' ? (d.partner_notes || []).find((n) => Number(n.id) === Number(o.id)) : null;
  const isEva = o.source === 'eva_chat';
  const sessions = isEva ? (d.transcript || []) : [];
  const msgCount = sessions.reduce((n, s) => n + (Number(s.message_count) || (s.messages || []).length || 0), 0);
  const inv = x && x.invite;
  return (
    <div data-testid="lead-source-card" data-record={o.record_type}
      style={{ border: '1px solid #f3f4f6', borderRadius: 10, padding: 12, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <SourceChip origin={o} bare />
        <span style={{ fontSize: 12, color: '#6b7280' }}>{DATE_LABEL[k] || 'Captured'} {fmt(o.created_at)}</span>
      </div>
      {o.partner_name && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <a href={`/admin/partners/partners?partner=${Number(o.partner_id)}`} style={linkStyle} data-testid="lead-card-partner">{o.partner_name}</a>
          {x && x.firm && x.firm.name && (
            <>
              {' '}<span style={{ color: '#9ca3af' }}>at</span>{' '}
              <a href={`/admin/partners/accounts?account=${Number(x.firm.account_id)}`} style={linkStyle} data-testid="lead-card-firm">{x.firm.name}</a>
            </>
          )}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        {o.record_type === 'broker_submission' && (
          <>
            <Row label="Submission">#{o.id}</Row>
            {x && x.fields_recorded != null && <Row label="Fields recorded">{x.fields_recorded}</Row>}
            {x && x.documents_uploaded != null && <Row label="Documents uploaded">{x.documents_uploaded}</Row>}
          </>
        )}
        {inv && (
          <Row label="Invite" testid="lead-card-invite">
            {inv.sent_at ? `Sent ${fmtSent(inv.sent_at)}` : 'Not sent'}
            {inv.times_sent > 0 && ` · sent ${inv.times_sent} ${inv.times_sent === 1 ? 'time' : 'times'}`}
            {inv.registered_at && ` · registered ${fmtShort(inv.registered_at)}`}
          </Row>
        )}
        {isEva && sessions.length > 0 && (
          <Row label="Conversation">
            {msgCount} message{msgCount === 1 ? '' : 's'} ·{' '}
            <button type="button" onClick={() => setShowChat((v) => !v)}
              style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5 }}>
              {showChat ? 'Hide conversation' : 'Show conversation'}
            </button>
          </Row>
        )}
      </div>
      {/* ⚠️ SAYS WHY THERE IS NOTHING: a chat lead with no session is a broken link, not an empty chat. */}
      {isEva && sessions.length === 0 && (
        <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>
          This lead is marked as coming from an Eva chat, but no session could be
          found for it — the link between the lead and the conversation is missing.
        </div>
      )}
      {showChat && sessions.map((sess) => (
        <div key={sess.session_uuid} style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>
            {fmtTime(sess.created_at)} · {sess.message_count} message{sess.message_count === 1 ? '' : 's'}
            {sess.escalated && <span style={{ color: '#b45309', fontWeight: 700 }}> · escalated{sess.escalation_reason ? ` (${sess.escalation_reason})` : ''}</span>}
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: '#fafafa' }}>
            {sess.messages.length === 0
              ? <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Session recorded with no messages stored.</div>
              : sess.messages.map((m, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.04em', color: m.role === 'user' ? '#1e40af' : '#6b7280', marginBottom: 2 }}>
                    {m.role === 'user' ? 'Them' : 'Eva'}
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.content}</div>
                </div>
              ))}
          </div>
        </div>
      ))}
      {/* The partner's own words at referral time — inside the partner-invite card. */}
      {note && (
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 10, marginTop: 8, fontSize: 13, color: '#374151' }}>
          <div style={{ whiteSpace: 'pre-wrap' }}>{note.notes}</div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>What {note.partner_name || 'the partner'} said · {fmt(note.created_at)}</div>
        </div>
      )}
      {/* 🔑 THE SUBMISSION RECORD — the staff view fix/partner-panels added; ?record=1 keeps it open after registration. */}
      {o.record_type === 'broker_submission' && (
        <div style={{ marginTop: 8, fontSize: 12.5 }}>
          <a href={`/admin/partners/submissions/${Number(o.id)}?record=1`} data-testid="lead-open-submission" style={linkStyle}>
            Open submission record →
          </a>
        </div>
      )}
    </div>
  );
}

// "Details differ" — name/phone/state on a source record vs the application, with the three logged actions.
function DetailsDiffer({ p, onDecide, busyKey }) {
  const diffs = p.field_differences || [];
  if (!diffs.length) return null;
  const originOf = (f) => (p.origins || []).find((o) => o.record_type === f.source_type && Number(o.id) === Number(f.source_id));
  const who = firstName((p.contact && p.contact.name && p.contact.name.value) || p.name) || 'they';
  const td = { padding: 8, borderTop: '1px solid #f3f4f6', verticalAlign: 'top' };
  const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', background: '#f9fafb' };
  return (
    <Section title="Details differ">
      <div data-testid="lead-details-differ">
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
          The referral and the application say different things. The application is what {who} confirmed.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr><th style={th}>Field</th><th style={th}>Source said</th><th style={th}>Application says</th></tr></thead>
          <tbody>
            {diffs.map((f) => {
              const o = originOf(f);
              const key = `${f.field}:${f.source_type}:${f.source_id}`;
              const busy = busyKey === key;
              return (
                <React.Fragment key={key}>
                  <tr data-testid="lead-diff-row" data-field={f.field}>
                    <td style={td}>
                      <b>{FIELD_LABEL[f.field]}</b>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        {SOURCE_LABEL[f.origin] || (o && SOURCE_LABEL[o.source]) || 'Other'}{o && o.partner_name ? ` · ${o.partner_name}` : ''}
                      </div>
                    </td>
                    <td style={td}>{f.source_value}</td>
                    <td style={td}>{f.application_value}</td>
                  </tr>
                  <tr>
                    <td colSpan={3} style={{ ...td, borderTop: 'none', paddingTop: 0 }}>
                      {f.decision ? (
                        <div data-testid="lead-diff-done" style={{ fontSize: 11, color: '#065f46', fontWeight: 700 }}>
                          ✓ {DONE_LABEL[f.decision.decision]} · {f.decision.basis === 'backcorrect_script' ? 'back-correct script' : firstName(f.decision.decided_by_name)} · {fmtSent(f.decision.decided_at)}
                        </div>
                      ) : !READ_ONLY && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                          <button type="button" disabled={busy} data-testid="lead-diff-use-application" onClick={() => onDecide(f, 'use_application')} style={smallBtn(true, busy)}>Use application value</button>
                          <button type="button" disabled={busy} data-testid="lead-diff-keep-source" onClick={() => onDecide(f, 'keep_source')} style={smallBtn(false, busy)}>Keep source value</button>
                          <button type="button" disabled={busy} data-testid="lead-diff-both-valid" onClick={() => onDecide(f, 'both_valid')} style={smallBtn(false, busy)}>Mark reviewed (both valid)</button>
                        </div>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// The consent label (footer, right) — today's per-channel facts; the tooltip carries today's sentence.
const NEVER_ASKED = 'Never asked — this person arrived by a route that captures no consent. Not a refusal.';
function ConsentLabel({ consentOrigins }) {
  const [tip, setTip] = useState(false);
  let label;
  let tipBody;
  if (consentOrigins.length === 0) {
    label = 'Consent: not captured on this route';
    tipBody = NEVER_ASKED;
  } else {
    const given = [['email', 'email', 'Email'], ['call', 'calls', 'Calls'], ['sms', 'texts', 'Texts']]
      .map(([k, word, title]) => ({ word, title, at: consentOrigins.map((o) => o.consent?.[k]).find(Boolean) }));
    const yes = given.filter((g) => g.at);
    const first = yes.map((g) => new Date(g.at)).sort((a, b) => a - b)[0];
    label = yes.length ? `Consent: ${yes.map((g) => g.word).join(', ')} (${fmtShort(first)})` : 'Consent: none given';
    tipBody = given.map((g) => `${g.at ? '✓' : '—'} ${g.title}${g.at ? ` · ${fmtTime(g.at)}` : ''}`).join('\n');
  }
  return (
    <span style={{ position: 'relative' }} data-testid="lead-consent">
      {label}
      <span tabIndex={0} role="img" aria-label="About consent" data-testid="lead-consent-info"
        onMouseEnter={() => setTip(true)} onMouseLeave={() => setTip(false)} onFocus={() => setTip(true)} onBlur={() => setTip(false)}
        style={{ color: '#9ca3af', cursor: 'help', marginLeft: 4 }}>ⓘ</span>
      {tip && (
        <span data-testid="lead-consent-tip" style={{
          position: 'absolute', bottom: 28, right: -6, width: 300, background: '#111827', color: '#fff', fontSize: 12,
          lineHeight: 1.5, borderRadius: 8, padding: '8px 10px', boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          whiteSpace: 'pre-line', zIndex: 2, textAlign: 'left',
        }}>{tipBody}</span>
      )}
    </span>
  );
}

function LeadDetail({ email, token, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState('');
  const [mail, setMail] = useState({ subject: '', body: '' });
  const [busy, setBusy] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await fetch(`${API}/api/admin/lead-captures/${encodeURIComponent(email)}/detail`, { headers: authHeaders(token) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setD(await r.json());
    } catch (e) { setErr(e.message); }
  }, [email, token]);
  useEffect(() => { load(); }, [load]);

  const post = async (path, payload, after) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/lead-captures/${encodeURIComponent(email)}/${path}`, {
        method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || `HTTP ${r.status}`);
      after();
      await load();
      onChanged && onChanged();
    } catch (e) { window.alert(e.message); } finally { setBusy(false); }
  };

  // The three "Details differ" actions — each one row in lead_field_decisions and a line in Activity.
  const decide = async (f, decision) => {
    const key = `${f.field}:${f.source_type}:${f.source_id}`;
    setBusyKey(key);
    try {
      const r = await fetch(`${API}/api/admin/lead-captures/${encodeURIComponent(email)}/field-decisions`, {
        method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ field: f.field, source_type: f.source_type, source_id: f.source_id, decision }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || `HTTP ${r.status}`);
      await load();
      onChanged && onChanged();
    } catch (e) { window.alert(e.message); } finally { setBusyKey(null); }
  };

  const p = d?.person;
  const panel = d?.panel || { origins: {}, team: {} };
  const xOf = (o) => panel.origins[`${o.record_type}:${o.id}`] || null;
  // Consent across every origin that could capture it. `capturable` false means
  // that path never asked — which is not the same as a refusal.
  const consentOrigins = p?.origins.filter((o) => o.consent_capturable) || [];
  const [tab, setTab] = useState('overview');
  const TABS = [
    ['overview', 'Overview'],
    ['comms', 'Communications'],
    ['activity', 'Activity'],
  ];

  // ── the header's ONE primary action ─────────────────────────────────────
  // Registered → their borrower file. Otherwise the newest origin whose invite staff can resend now: a broker submission
  // (POST /api/admin/partners/submissions/:id/resend-invite — fix/partner-panels' staff route, inviteBorrowerIfViewable
  // with force) or a partner invite (POST /api/admin/borrowers/pending-invites/:id/resend, its 24h cooldown).
  const fileId = p && p.registered ? (p.registered_user_id || d.borrower?.id || null) : null;
  const resendOrigin = p && !fileId ? [...p.origins]
    .filter((o) => { const x = xOf(o); return x && x.resend && (x.resend.ok || x.resend.cooldown_hours_left > 0); })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] : null;
  const resendX = resendOrigin ? xOf(resendOrigin).resend : null;
  const cooling = resendX && resendX.cooldown_hours_left > 0;
  const resend = async () => {
    if (!resendOrigin) return;
    setBusy(true);
    try {
      // ⚠️ TWO LITERAL CALLS, NOT A URL VARIABLE: test/partnerSuccessReach reads every fetch on the page to prove
      // each write is denied to partner_success, and it can only read a literal.
      const r = resendOrigin.record_type === 'broker_submission'
        ? await fetch(`${API}/api/admin/partners/submissions/${Number(resendOrigin.id)}/resend-invite`, { method: 'POST', headers: authHeaders(token) })
        : await fetch(`${API}/api/admin/borrowers/pending-invites/${Number(resendOrigin.id)}/resend`, { method: 'POST', headers: authHeaders(token) });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || `HTTP ${r.status}`);
      await load();
      onChanged && onChanged();
    } catch (e) { window.alert(e.message || 'That did not work.'); } finally { setBusy(false); }
  };
  const primaryBtn = (disabled) => ({
    padding: '8px 14px', fontSize: 12, fontWeight: 700, borderRadius: 8, border: 'none', whiteSpace: 'nowrap',
    background: disabled ? '#e5e7eb' : '#3a6ad9', color: disabled ? '#9ca3af' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
  });
  const action = !p ? null : fileId ? (
    <a href={`/admin/borrowers/${Number(fileId)}`} data-testid="lead-open-borrower" style={{ ...primaryBtn(false), textDecoration: 'none', display: 'inline-block' }}>
      Open borrower file
    </a>
  ) : (resendOrigin && !READ_ONLY) ? (
    <button type="button" data-testid="lead-resend-invite" disabled={busy || cooling} onClick={resend} style={primaryBtn(busy || cooling)}
      title={cooling ? `Wait ${resendX.cooldown_hours_left}h before resending` : undefined}>
      {busy ? '…' : cooling ? `Resend in ${resendX.cooldown_hours_left}h` : 'Resend invite'}
    </button>
  ) : null;

  const st = p ? leadStatus(p) : null;
  const shown = (f) => (p && p.contact && p.contact[f] ? p.contact[f].value : p && p[f]) || null;
  const name = shown('name');
  const phone = shown('phone');
  const state = shown('state');

  // ── the footer: queue + Archive (left), consent (right) ──────────────────
  const queueText = !p ? '' : p.has_application
    ? 'Has an application, so stays in the pipeline.'
    : p.all_origins_archived
      ? 'Archived — hidden from the default queue.'
      : (p.archived_origins > 0 ? `${p.archived_origins} of ${p.origins.length} records archived.` : 'Active in the queue.');
  const footer = !p ? null : (
    <div data-testid="lead-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 12, color: '#6b7280' }}>
      <span data-testid="lead-queue">
        {queueText}
        {!READ_ONLY && !p.has_application && p.can_archive && (
          <button type="button" disabled={busy} onClick={() => post('archive', {}, () => {})} data-testid="lead-archive"
            style={{ marginLeft: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, borderRadius: 8,
              border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: busy ? 'not-allowed' : 'pointer' }}>
            {busy ? '…' : 'Archive'}
          </button>
        )}
        {!READ_ONLY && !p.has_application && p.can_unarchive && (
          <button type="button" disabled={busy} onClick={() => post('unarchive', {}, () => {})}
            style={{ marginLeft: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, borderRadius: 8,
              border: '1px solid #e5e7eb', background: '#fff', color: '#3a6ad9', cursor: busy ? 'not-allowed' : 'pointer' }}>
            {busy ? '…' : 'Restore to queue'}
          </button>
        )}
      </span>
      <ConsentLabel consentOrigins={consentOrigins} />
    </div>
  );

  const team = panel.team || {};
  return (
    <RightDrawer
      open
      onClose={onClose}
      width={560}
      title={d ? (
        <>
          <Field value={name} label="name" />
          <span data-testid="lead-status-pill" style={{ background: st.bg, color: st.fg, borderRadius: 100, padding: '3px 10px',
            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 6, verticalAlign: 2,
            border: p.registered === false ? '1px solid #e5e7eb' : 'none' }}>{st.text}</span>
        </>
      ) : 'Lead'}
      subtitle={d ? (
        <span data-testid="lead-contact-line">
          {p.email}
          {phone && <> · {phone}</>}
          {state
            ? <> · <span style={{ color: p.out_of_area ? '#b91c1c' : undefined }}
                title={p.out_of_area ? `Outside ${d.lending_states.join(' / ')}` : undefined}>{state}</span></>
            : <> · <span style={{ color: '#b45309' }} title="No state captured — eligibility unknown">state unknown</span></>}
        </span>
      ) : ''}
      actions={action}
      footer={footer}
      tabs={d ? (
          <div style={{ display: 'flex', gap: 2, padding: '0 20px',
                        borderBottom: '1px solid #e5e7eb' }}>
            {TABS.map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`lead-tab-${k}`}
                style={{ border: 'none', background: 'none', cursor: 'pointer',
                  padding: '10px 12px', fontSize: 12, fontWeight: 700,
                  color: tab === k ? '#3a6ad9' : '#6b7280',
                  borderBottom: `2px solid ${tab === k ? '#3a6ad9' : 'transparent'}`,
                  marginBottom: -1 }}>
                {label}
              </button>
            ))}
          </div>
      ) : null}
    >
      {/* ⚠️ THE DRAWER OWNS THE SCROLL NOW. This was a second scrolling box
          inside the panel; two nested scroll regions is how a touch drag ends up
          moving the wrong one. */}
      <div style={{ padding: '4px 0 32px' }}>

        {err && <div style={{ color: '#b91c1c', fontSize: 13 }}>{err}</div>}
        {!d && !err && <div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div>}

        {d && tab === 'overview' && (
          <>
            {panel.next_step && (
              <Section title="Next step">
                <div data-testid="lead-next-step" style={{ fontSize: 13, color: '#374151' }}>{panel.next_step}</div>
              </Section>
            )}

            <DetailsDiffer p={p} onDecide={decide} busyKey={busyKey} />

            <Section title="Where they came from">
              {chipOrigins(p).map((o, i) => <SourceCard key={i} o={o} x={xOf(o)} d={d} />)}
              {/* ⚠️ A HIDDEN ROW IS STATED, NOT SILENTLY DROPPED. */}
              {p.hidden_registration_origins > 0 && (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {p.hidden_registration_origins === 1 ? 'One legacy' : `${p.hidden_registration_origins} legacy`}{' '}
                  <code style={{ fontSize: 10 }}>registration</code> row is on file and not shown
                  as an origin — registering is not where someone came from. Status covers it.
                </div>
              )}
            </Section>

            <Section title="Team">
              <div data-testid="lead-team">
                <Row label="Partner Success">
                  {team.partner_success
                    ? <a href={`/admin/partners/partners?partner=${Number(team.partner_success.partner_id)}`} style={linkStyle}>{team.partner_success.name}</a>
                    : <span style={{ color: '#9ca3af' }}>—</span>}
                </Row>
                <Row label="MLO of record" testid="lead-team-mlo">
                  {team.mlo_of_record
                    ? `${team.mlo_of_record.name} · NMLS ${team.mlo_of_record.nmls}${p.registered && team.mlo_of_record.assigned_at ? ` · assigned ${fmtShort(team.mlo_of_record.assigned_at)}` : ''}`
                    : <span style={{ color: '#9ca3af' }}>—</span>}
                </Row>
              </div>
            </Section>
          </>
        )}

        {d && tab === 'activity' && (
          <>
            <Section title="History">
              {d.timeline.length === 0
                ? <div style={{ fontSize: 12, color: '#9ca3af' }}>Nothing recorded yet.</div>
                : d.timeline.map((t, i) => (
                    <div key={i} data-testid="lead-history-line" style={{ display: 'flex', gap: 8, fontSize: 12, color: '#374151', padding: '4px 0' }}>
                      <span style={{ color: '#9ca3af' }}>{KIND_ICON[t.kind] || '•'}</span>
                      <span style={{ flex: 1 }}>{t.detail}</span>
                      <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>{t.at ? fmt(t.at) : '—'}</span>
                    </div>
                  ))}
            </Section>

            <Section title="Notes">
              {/* 🔑 THE HISTORY STAYS, THE COMPOSER GOES. "Everything on those two
                  pages" is a READ grant — the existing notes are context a rep
                  needs, and writing one is the edit access that was withheld. */}
              {!READ_ONLY && (
                <>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    placeholder="Add a note…" style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
                  <button disabled={busy || !note.trim()} onClick={() => post('notes', { note }, () => setNote(''))}
                    style={btn(busy || !note.trim())}>Save note</button>
                </>
              )}
              <div style={{ marginTop: 10 }}>
                {d.notes.map((n) => (
                  <div key={n.id} style={{ borderTop: '1px solid #f3f4f6', padding: '8px 0', fontSize: 13, color: '#374151' }}>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{n.note}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{n.created_by || 'staff'} · {fmtTime(n.created_at)}</div>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}

        {/* ── 🚨 TWO LOGS, BECAUSE THEY ANSWER DIFFERENT QUESTIONS ──────
            lead_drip_emails_sent is what the CAMPAIGN decided to send —
            sequence, day, key. email_log is what the PROVIDER did with it —
            delivery status, bounce reason. A drip row with no matching
            delivery row means the campaign fired and nothing arrived, and
            that is invisible if you only render one of them. */}
        {d && tab === 'comms' && (
          <>
            <Section title="Drip emails">
              {(!d.drip_emails || d.drip_emails.length === 0)
                ? <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    No drip emails sent to this lead.
                  </div>
                : d.drip_emails.map((e, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12,
                    borderBottom: '1px solid #f3f4f6', padding: '6px 0', fontSize: 12 }}>
                    <span style={{ color: '#374151' }}>
                      <strong>{e.sequence}</strong> · day {e.sequence_day}
                      <span style={{ color: '#9ca3af' }}> · {e.source}</span>
                    </span>
                    <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>{fmtTime(e.sent_at)}</span>
                  </div>
                ))}
            </Section>

            <Section title="Delivery log">
              {(!d.delivery_log || d.delivery_log.length === 0)
                ? <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    Nothing recorded against this address.
                  </div>
                : d.delivery_log.map((e, i) => {
                  // The provider's own words. A bounce is the one thing here
                  // somebody has to act on, so it is the one thing coloured.
                  const bad = e.delivery_status === 'bounced' || e.delivery_status === 'complained'
                           || e.status === 'failed';
                  return (
                    <div key={i} style={{ borderBottom: '1px solid #f3f4f6', padding: '7px 0', fontSize: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: '#374151', overflowWrap: 'anywhere' }}>{e.subject || '(no subject)'}</span>
                        <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>{fmtTime(e.sent_at)}</span>
                      </div>
                      <div style={{ fontSize: 11, marginTop: 2, color: bad ? '#b91c1c' : '#9ca3af' }}>
                        {e.category ? `${e.category} · ` : ''}
                        {e.delivery_status || e.status || 'sent'}
                        {e.bounce_reason ? ` — ${e.bounce_reason}` : ''}
                      </div>
                    </div>
                  );
                })}
            </Section>

            {/* 🚨 AN OUTBOUND CHANNEL IS NOT A READ. This is the one write on
                this page whose absence from the grant was explicit: sending a
                borrower an email is the capability /tickets accidentally handed
                this role in August, and the reason the route list is method-exact. */}
            {!READ_ONLY && (
            <Section title="Send an email">
              {/* Consent is shown, not enforced — a 1:1 operational reply is not
                  marketing. The admin sees the state before sending. */}
              {consentOrigins.length === 0 && (
                <div style={{ fontSize: 11, color: '#b45309', marginBottom: 6 }}>
                  No consent was ever captured for this person. Operational replies are fine; treat anything promotional with care.
                </div>
              )}
              {/* ⚠️ OUT OF AREA IS SURFACED, NOT ENFORCED — the same call consent
                  already made, for the same reason. */}
              {p?.out_of_area === true && (
                <div style={{ fontSize: 11, color: '#b91c1c', marginBottom: 6 }}>
                  Doorly cannot lend in {state}. Operational replies are fine; anything promotional isn&rsquo;t.
                </div>
              )}
              <input value={mail.subject} onChange={(e) => setMail((m) => ({ ...m, subject: e.target.value }))}
                placeholder="Subject" style={{ ...inputStyle, width: '100%', marginBottom: 6 }} />
              <textarea value={mail.body} onChange={(e) => setMail((m) => ({ ...m, body: e.target.value }))} rows={4}
                placeholder="Message…" style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
              <button disabled={busy || !mail.subject.trim() || !mail.body.trim()}
                onClick={() => post('email', mail, () => setMail({ subject: '', body: '' }))}
                style={btn(busy || !mail.subject.trim() || !mail.body.trim())}>Send email</button>
              <div style={{ marginTop: 10 }}>
                {d.emails.map((e) => (
                  <div key={e.id} style={{ borderTop: '1px solid #f3f4f6', padding: '8px 0', fontSize: 12, color: '#374151' }}>
                    <strong>{e.subject}</strong>
                    <div style={{ whiteSpace: 'pre-wrap', color: '#6b7280' }}>{e.body}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{e.sent_by || 'staff'} · {fmtTime(e.sent_at)}</div>
                  </div>
                ))}
              </div>
            </Section>
            )}
          </>
        )}
      </div>
    </RightDrawer>
  );
}

// The panel's section heading. Each tab renders its own block now (the approved redesign merged Overview and Source),
// so a section no longer carries its tab.
function Section({ title, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}
const btn = (disabled) => ({
  marginTop: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, borderRadius: 8, border: 'none',
  background: disabled ? '#e5e7eb' : '#3a6ad9', color: disabled ? '#9ca3af' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export default function AdminLeadCaptures() {
  // doorly_admin_token — the key every other admin page uses. This read
  // 'adminToken' || 'token', neither of which exists, so the page sent
  // 'Authorization: Bearer null' and got 401 'Invalid or expired token.'
  const token = localStorage.getItem('doorly_admin_token');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // Defaults to NOT CONVERTED: this is a work queue, and a converted lead is a
  // record rather than a task. Converted rows stay one click away.
  const [filters, setFilters] = useState({ source: '', converted: 'false', q: '', differs: '' });
  // `busy` lived here to disable a row's action buttons mid-request. The
  // actions moved into the detail panel, which tracks its own busy state, so
  // the table no longer has anything to disable.
  const [openEmail, setOpenEmail] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
      const r = await fetch(`${API}/api/admin/lead-captures${qs ? `?${qs}` : ''}`, { headers: authHeaders(token) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) { setErr(e.message); }
  }, [token, filters]);

  useEffect(() => { load(); }, [load]);

  const c = data?.counts;

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: 1500 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 6 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: 0 }}>Leads</h1>
          {c && <span style={{ color: '#6b7280', fontSize: 13 }}>
            {c.people} {c.people === 1 ? 'person' : 'people'} from {c.records} records
          </span>}
        </div>
        <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 20px', maxWidth: 760 }}>
          People who have given Doorly contact details. Someone who has <em>submitted an
          application</em> lives under Applications — this page never shows credit, price or
          employment.
        </p>

        {/* 🔑 THE "DETAILS DIFFER" PILL (approved 2026-11-02): it says what it means and opens the list it counts —
            "Review" sets the filter (any status, since these people have applications), "✕ clear" removes it. It
            replaces the old "where a source record differs" pill, which compared conversion flags, not details. */}
        {c && (c.details_differ > 0 || filters.differs === '1' || c.multi_partner > 0 || c.out_of_area > 0) && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            {(c.details_differ > 0 || filters.differs === '1') && (
              <Banner tone="warn">
                <span data-testid="leads-differ-pill">
                  {c.details_differ} {c.details_differ === 1 ? 'lead' : 'leads'} where details from the referral don&rsquo;t match the application.{' '}
                  <button type="button" data-testid="leads-differ-review"
                    onClick={() => setFilters((f) => ({ ...f, differs: '1', converted: '' }))}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>
                    Review
                  </button>
                  {filters.differs === '1' && (
                    <button type="button" data-testid="leads-differ-clear"
                      onClick={() => setFilters((f) => ({ ...f, differs: '' }))}
                      style={{ background: 'none', border: 'none', padding: 0, marginLeft: 6, color: '#6b7280', font: 'inherit', cursor: 'pointer' }}>
                      ✕ clear
                    </button>
                  )}
                </span>
              </Banner>
            )}
            {c.multi_partner > 0 && <Banner tone="alert">{c.multi_partner} invited by more than one partner</Banner>}
            {c.out_of_area > 0 && <Banner tone="warn">{c.out_of_area} outside {data.lending_states.join(' / ')}</Banner>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            placeholder="Search name or email…" value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            style={inputStyle}
          />
          <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))} style={inputStyle}>
            <option value="">All sources</option>
            {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            {/* ⚠️ "Other" is a RENDERED label with no stored value behind it — it
                is what sourceKey() folds every untrackable source into. Deriving
                options from SOURCE_LABEL alone left a chip on screen that could
                not be filtered for. The backend matches the same fold. */}
            <option value="other">Other</option>
          </select>
          <select value={filters.converted} onChange={(e) => setFilters((f) => ({ ...f, converted: e.target.value }))} style={inputStyle}>
            <option value="">Any status</option>
            <option value="false">Not converted</option>
            <option value="true">Converted</option>
            {/* A third state, not a flavour of unconverted — an archived
                referral was deliberately taken out of the queue, so it stays
                out of the other views entirely. */}
            <option value="archived">Archived</option>
          </select>
          {/* 🔑 THE SAME FILTER AS THE PILL'S "Review", among the others. */}
          <select value={filters.differs} data-testid="leads-differ-filter"
            onChange={(e) => setFilters((f) => ({ ...f, differs: e.target.value, ...(e.target.value === '1' ? { converted: '' } : {}) }))}
            style={{ ...inputStyle, ...(filters.differs === '1' ? { color: '#b45309', fontWeight: 600 } : {}) }}>
            <option value="">Any details</option>
            <option value="1">Details differ</option>
          </select>
          <a href={`${API}/api/admin/export/lead-captures`} style={{ ...inputStyle, textDecoration: 'none', color: '#3a6ad9', fontWeight: 600, lineHeight: '22px' }}>
            Export CSV
          </a>
        </div>

        {err && <div style={{ color: '#b91c1c', fontSize: 13 }}>Could not load: {err}</div>}
        {!data && !err && <div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div>}

        {data && data.people.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
            No leads match these filters.
          </div>
        )}

        {data && data.people.length > 0 && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                  {[
                    { h: 'Person' },
                    // ⚠️ STATUS FIRST, matching the cell order below. The Status
                    // cell sits after the Person cell, so rows rendered
                    // Person | Status | Sources under headers reading
                    // Person | Sources | Status. Swapping two labels is smaller
                    // than moving a JSX block, and a header disagreeing with its
                    // column is the actual defect.
                    { h: 'Status', tip: 'Whether they created an account. Registration is a STATUS, not a source — something that happened to the lead, not where they came from.' },
                    { h: 'Sources', tip: 'Where this person came from. Only Eva chat and partner invites are attributable today; everything else reads Other until campaigns are trackable.' },
                    { h: 'State' },
                    { h: 'Conversion', tip: 'Full application has been submitted and pending verification' },
                    { h: 'Created', tip: 'When this person was first captured. Newest first.' },
                    { h: 'Last activity', tip: 'The last time a Doorly user touched this lead — a note, an email, or a resent invite. Not what the lead themselves did.' },
                  ].map(({ h, tip }) => (
                    <th key={h} style={thStyle} title={tip || undefined}>
                      {h}
                      {tip && <span style={{ marginLeft: 4, color: '#9ca3af', cursor: 'help' }}>ⓘ</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.people.map((p) => {
                  // A converted person is a record, not a work item. The row is
                  // muted and carries no actions; it links to the borrower file
                  // where the application actually lives.
                  const locked = p.any_converted && !p.conversion_conflict;
                  const userId = p.origins.map((o) => o.converted_user_id).find(Boolean);
                  return (
                    <tr key={p.email}
                        onClick={() => setOpenEmail(p.email)}
                        style={{ borderTop: '1px solid #f3f4f6', background: locked ? '#fafafa' : '#fff', cursor: 'pointer' }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, color: locked ? '#6b7280' : '#111827' }}>
                          <Field value={p.name} label="name" />
                        </div>
                        <div style={{ color: '#6b7280', fontSize: 12 }}>{p.email}</div>
                        {/* 🔑 FOR A REGISTERED PERSON, PHONE AND STATE ARE THE APPLICATION'S (server: contact) — marked
                            "from application" when no source had one. */}
                        {(() => {
                          const ph = p.contact && p.contact.phone ? p.contact.phone : { value: p.phone, from_application: false };
                          return (
                            <div style={{ color: '#9ca3af', fontSize: 12, marginTop: ph.from_application ? 3 : 0 }}>
                              <Field value={ph.value} label="phone" />
                              {ph.value && ph.from_application && <span data-testid="leads-from-application" style={{ fontSize: 11 }}> · from application</span>}
                            </div>
                          );
                        })()}
                        {p.details_differ && (
                          <div style={{ marginTop: 6 }}>
                            {/* Hover: in plain words, which fields differ and which record each value came from. Staff only. */}
                            <span data-testid="leads-differ-tag" title={p.details_differ_hover || undefined} style={{
                              display: 'inline-block', fontSize: 10.5, fontWeight: 700, color: '#b45309', background: '#fffbeb',
                              border: '1px solid #fde68a', borderRadius: 100, padding: '1px 8px', cursor: 'help',
                            }}>Details differ</span>
                          </div>
                        )}
                        {p.origin_count > 1 && (
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                            {p.origin_count} records merged
                          </div>
                        )}
                      </td>
                      {/* ⚠️ STATUS — its own column, because registering is not a
                          source. Two explicit states; never a blank cell. */}
                      <td style={tdStyle}>
                        {(() => { const st = leadStatus(p); return (
                          <span style={{
                            background: st.bg, color: st.fg, borderRadius: 100,
                            padding: '3px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                          }}>{st.text}</span>
                        ); })()}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          {/* ⚠️ NO ACTION BUTTONS IN THE ROW. They live in the
                              detail panel now: a queue is for scanning, and a
                              destructive action one mis-tap from a row click is
                              the wrong affordance. The chip still shows where
                              each origin came from, and an archived one says so. */}
                          {/* ⚠️ ONE ARRIVAL, RECORDED TWICE. A partner invites a
                              buyer, the buyer registers hours later, and the
                              registration hook writes a SECOND origin row — so
                              the person showed two source chips for one arrival,
                              the second reading "Other" as if it were an
                              untracked campaign.

                              🚨 THE SUPPRESSION USED TO LIVE HERE AND ONLY HERE,
                              which is why Loc Tran read as one clean "Partner
                              invite" in this table and showed "Partner invite" +
                              "Other" the moment anyone expanded him. It is
                              server-side now (display_origins) so both surfaces
                              agree.

                              ⚠️ AND registered_at NO LONGER DRIVES STATUS — it
                              never successfully did. Status is derived from
                              `users` by utils/leadRegistration.js. */}
                          {chipOrigins(p).map((o, i) => (
                            <div key={i}>
                              <SourceChip origin={o} />
                              {o.archived && (
                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#6b7280' }}>
                                  archived
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                        {/* Two different partners invited the same person. Not a
                            duplicate to hide — compensation pays exactly one. */}
                        {p.multi_partner && (
                          <div style={{ marginTop: 6, fontSize: 11, color: '#b91c1c', fontWeight: 700 }}>
                            ⚠ Multiple partners
                            {p.registered_through_partner_id && (
                              <div style={{ fontWeight: 500, color: '#6b7280' }}>
                                registered through partner #{p.registered_through_partner_id}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {(p.contact && p.contact.state ? p.contact.state.value : p.state) ? (
                          <>
                            <span style={{ fontWeight: 700, color: p.out_of_area ? '#b91c1c' : '#111827' }}>{p.contact && p.contact.state ? p.contact.state.value : p.state}</span>
                            {p.contact && p.contact.state && p.contact.state.from_application && (
                              <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>from application</div>
                            )}
                            {p.out_of_area && (
                              <div style={{ fontSize: 11, color: '#b91c1c', fontWeight: 600 }}>
                                Outside {data.lending_states.join(' / ')}
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#b45309', fontSize: 12, fontWeight: 600 }} title="No state captured — eligibility unknown">
                            unknown
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}><ConversionCell person={p} /></td>
                      <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {fmt(p.first_seen)}
                      </td>
                      <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {p.last_activity_at
                          ? fmt(p.last_activity_at)
                          // Neutral grey (the page's existing muted grey), not amber — approved 2026-11-02.
                          : <span style={{ color: '#6b7280', fontSize: 12 }} title="Nobody at Doorly has worked this lead yet">never touched</span>}
                        {locked && userId && (
                          <div style={{ marginTop: 6 }}>
                            <a href={`/admin/borrowers/${userId}`} style={{ color: '#3a6ad9', fontSize: 12, fontWeight: 600 }}>
                              Borrower file →
                            </a>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {openEmail && (
        <LeadDetail email={openEmail} token={token}
          onClose={() => setOpenEmail(null)} onChanged={load} />
      )}
    </AdminLayout>
  );
}

function Banner({ tone, children }) {
  const s = tone === 'alert'
    ? { bg: '#fef2f2', fg: '#b91c1c', bd: '#fecaca' }
    : { bg: '#fffbeb', fg: '#b45309', bd: '#fde68a' };
  return (
    <div style={{
      background: s.bg, color: s.fg, border: `1px solid ${s.bd}`,
      borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600,
    }}>{children}</div>
  );
}

const inputStyle = {
  border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 11px',
  fontSize: 13, color: '#111827', background: '#fff', outline: 'none',
};
const thStyle = { padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' };
const tdStyle = { padding: '12px 14px', verticalAlign: 'top' };
